import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { createUser, updateUser, deleteUser } from '../services/userService';
import { queryKeys } from '../constants/queryKeys';

/**
 * useCreateUser — Create a new user
 *
 * Usage in AddUser.jsx:
 *   const createMutation = useCreateUser();
 *
 *   const onSubmit = (data) => {
 *     createMutation.mutate(data);
 *   };
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => createUser(data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User created successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to create user. Please try again.';
      toast.error(message);
    },
  });
};

/**
 * useUpdateUser — Update an existing user
 *
 * Usage:
 *   const updateMutation = useUpdateUser();
 *   updateMutation.mutate({ id: 3, data: formData });
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateUser(id, data),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User updated successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to update user. Please try again.';
      toast.error(message);
    },
  });
};

/**
 * useDeleteUser — Delete a user
 *
 * Usage:
 *   const deleteMutation = useDeleteUser();
 *   deleteMutation.mutate(userId);
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteUser(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success('User deleted successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to delete user. Please try again.';
      toast.error(message);
    },
  });
};
